import { Box, Button, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import type { EpiChatSendPayload, EpiCheckpoint } from '../types/epi-chat-types';

/**
 * Structured consent signal sent on the ``choice`` wire field when the
 * researcher clicks Process, followed by the checkpoint id — the same shape as
 * ``checkpoint_approve:<checkpointId>``. The backend does not act on it: the
 * model starts the run from the click's message text.
 */
export const CHECKPOINT_PROCESS_CHOICE_PREFIX = 'checkpoint_process:';

interface ProcessAnalysisPromptProps {
  checkpoint: EpiCheckpoint;
  onAnswer: (answer: EpiChatSendPayload) => void;
  /** Analysis name from the sibling analysis resource card, when one rendered. */
  assetName: string | null;
  /** Render the Process button. True only while this checkpoint is the latest,
   *  unanswered message — a newer message supersedes the pending gate. */
  showProcess: boolean;
  disabled?: boolean;
}

/**
 * Prompt for the ``process_analysis`` checkpoint: a review line plus a single
 * Process button. Processing is started by a chat message carrying
 * ``{ text, checkpointId, choice }``; there is no Edit affordance — to change
 * the analysis the researcher types into chat.
 */
export const ProcessAnalysisPrompt = ({
  checkpoint,
  onAnswer,
  assetName,
  showProcess,
  disabled,
}: ProcessAnalysisPromptProps) => {
  const intl = useIntl();
  return (
    <Box data-testid="process-analysis-prompt">
      <Typography
        variant="body2"
        data-testid="process-analysis-review-prompt-copy"
        sx={{ marginBottom: showProcess ? 1.5 : 0 }}
      >
        {assetName
          ? intl.formatMessage(
              {
                id: 'epiChat.processAnalysis.reviewPrompt',
                defaultMessage: 'The analysis {name} is ready. Would you like to process it?',
              },
              { name: assetName }
            )
          : intl.formatMessage({
              id: 'epiChat.processAnalysis.reviewPromptGeneric',
              defaultMessage: 'The analysis is ready. Would you like to process it?',
            })}
      </Typography>
      {showProcess && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          data-testid="process-analysis-button"
          disabled={disabled}
          onClick={() =>
            onAnswer({
              text: intl.formatMessage({
                id: 'epiChat.processAnalysis.processedMessage',
                defaultMessage: 'Process this analysis.',
              }),
              checkpointId: checkpoint.checkpointId,
              choice: `${CHECKPOINT_PROCESS_CHOICE_PREFIX}${checkpoint.checkpointId}`,
            })
          }
        >
          {intl.formatMessage({
            id: 'epiChat.processAnalysis.processButton',
            defaultMessage: 'Process',
          })}
        </Button>
      )}
    </Box>
  );
};
